// Delivery intake/transition state machine — pure logic extracted from
// Deliveries.tsx so the routing rules are unit-testable (PACK-002…007).
//
//   intake_path  → initial status        (initialStatusForPath)
//   current status → operator actions     (deliveryActions)
//
// The "drench before colour-up" rule (PACK-006) is structural: OUT_FOR_COLOUR_UP
// is only reachable from DRENCHED.

import type { DeliveryStatus, IntakePath } from '@/types/database'

// Intake path → the status a new delivery lands in (mig 119). Legacy IntakePath
// values (DRENCHING / EXTERNAL_COLOUR_UP / DIRECT_TO_PRODUCTION) are mapped to
// their closest current equivalent so a pre-backfill row still routes correctly.
export function initialStatusForPath(path: IntakePath): DeliveryStatus {
  switch (path) {
    case 'NOT_DRENCHED':         return 'AWAITING_DRENCH'
    case 'DRENCHED':             return 'DRENCHED'
    case 'REBINNED':             return 'IN_STOCK'
    // Fruit that is never drenched goes straight to the cold room (mig 20261025191001).
    case 'DIRECT_TO_COLDROOM':   return 'IN_STOCK'
    case 'DRENCHING':            return 'AWAITING_DRENCH'
    case 'EXTERNAL_COLOUR_UP':   return 'DRENCHED'
    case 'DIRECT_TO_PRODUCTION': return 'IN_STOCK'
  }
}

export type DeliveryActionIcon = 'check' | 'rotate' | null

export interface DeliveryAction {
  /** Status this action transitions the delivery to. */
  to: DeliveryStatus
  /** Button label. */
  label: string
  /** Reason recorded in status_history for the transition. */
  reason: string
  /** Icon hint for the renderer ('check' = CheckCircle, 'rotate' = RotateCcw). */
  icon: DeliveryActionIcon
}

// Forward state machine for the delivery action panel: given a delivery's current
// status (+ intake_path, used only to route legacy RECEIVED rows), the operator
// transitions available. An empty list = no panel actions (DRAFT/IN_STOCK/TIPPED).
export function deliveryActions(
  status: DeliveryStatus,
  intakePath: IntakePath | null | undefined,
): DeliveryAction[] {
  switch (status) {
    // RECEIVED is legacy (current intake no longer sets it); infer from path.
    case 'RECEIVED':
      if (intakePath === 'NOT_DRENCHED' || intakePath === 'DRENCHING' || intakePath === 'EXTERNAL_COLOUR_UP') {
        return [{ to: 'AWAITING_DRENCH', label: 'Start Drenching', reason: 'Sent for drenching', icon: null }]
      }
      return [{ to: 'IN_STOCK', label: 'Mark In Stock', reason: 'Moved directly to stock', icon: 'check' }]
    case 'AWAITING_DRENCH':
      return [{ to: 'DRENCHED', label: 'Drenching Done', reason: 'Drenching complete', icon: 'check' }]
    // DRENCHED — operator picks the destination; colour-up is ONLY reachable here.
    case 'DRENCHED':
      return [
        { to: 'IN_STOCK',          label: 'Mark In Stock',  reason: 'Moved to stock',      icon: 'check' },
        { to: 'OUT_FOR_COLOUR_UP', label: 'Send Colour-Up', reason: 'Sent for colour-up',  icon: null },
      ]
    case 'OUT_FOR_COLOUR_UP':
      return [{ to: 'IN_STOCK', label: 'Received from Colour-Up', reason: 'Returned from colour-up — moved to stock', icon: 'rotate' }]
    default:
      return []
  }
}

// One step BACK on the same machine (mig 20261019120000 revert_delivery_status).
// A mis-tap on "Mark In Stock" or "Drenching Done" used to be permanent. The
// server holds the same map and refuses once the delivery has tips; this copy
// only decides whether to show the button and what it will do.
//   DRENCHED          → AWAITING_DRENCH
//   OUT_FOR_COLOUR_UP → DRENCHED
//   IN_STOCK          → DRENCHED, but only when the intake path went through
//                       drenching; a REBINNED / direct-to-production /
//                       straight-to-cold-room delivery started IN_STOCK and has
//                       nothing to go back to.
// null = no revert offered.
export function deliveryRevert(
  status: DeliveryStatus,
  intakePath: IntakePath | null | undefined,
): DeliveryStatus | null {
  switch (status) {
    case 'DRENCHED':          return 'AWAITING_DRENCH'
    case 'OUT_FOR_COLOUR_UP': return 'DRENCHED'
    case 'IN_STOCK':
      return (intakePath === 'NOT_DRENCHED' || intakePath === 'DRENCHED'
           || intakePath === 'DRENCHING'    || intakePath === 'EXTERNAL_COLOUR_UP')
        ? 'DRENCHED'
        : null
    default:
      return null
  }
}
